import { SingleValueCardColor, SingleValueCardType } from "constants/index";

export type ColumnPeriodT = {
  name: string;
  period: [string, string];
};

export interface PerformanceComparisonTableProps {
  dateRange: [string | null, string | null];
  onColumnPeriodsChange: (periods: ColumnPeriodT[]) => void;
}

export interface PerformanceColumnSelectorModalProps {
  open: boolean;
  onClose: () => void;
  columnPeriods: ColumnPeriodT[];
  onChange: (columns: ColumnPeriodT[]) => void;
  dateRange: [string | null, string | null];
}

export interface StatisticState {
  dateRange: [string | null, string | null];
}

export interface IncomeSummaryProps {
  columnPeriods: ColumnPeriodT[];
}

export interface OtherStatisticProps {
  dateRange: [string | null, string | null];
  columnPeriods: ColumnPeriodT[];
}

// Report related interfaces
export interface ReportValue {
  date: string;
  beginning_value: number;
  value_change: number;
  cashflows: number;
  dividends: number;
  closing_price: number;
  nav_price: number;
  unit_balance: number;
  factor: number;
  stock_factor: number;
  time_weighted_return: number;
  time_weighted_return_stock: number;
}

export interface IncomeSummaryItem {
  name: string;
  value: ReportValue;
}

export interface GraphData {
  date: string;
  stock_value: number;
  option_value: number;
}

export interface ReportResponse {
  status: string;
  data: {
    report_data: string;
    income_summary: IncomeSummaryItem[];
    graph_data: GraphData[];
  };
  message: string;
}

export interface ReportParams {
  account_ids: number[];
  strategy_ids: number[];
  periods: {
    name: string;
    period: [string, string];
  }[];
}

export interface NewReportRequest {
  orion_account_id: number;
  strategy_id: number;
  start_date: string;
  end_date: string;
  global_cumulative_period?: [string, string];
  report_type: ReportType;
  exclude_transactions?: OrionTransaction["id"][];
}

export interface NewReportSingleInfoCards {
  card_1: number;
  card_2: number;
  card_3: number;
  card_4: number;
  card_5: number;
  card_6: number;
}

export interface NewReportThemedCards {
  keep_your_shares: {
    starting_shares: number;
    current_shares: number;
    stock_price_start: number;
    stock_price_now: number;
    stock_only_return: number;
    upside_participation: number;
  };
  generate_income: {
    option_income_period: number;
    dividend_income_period: number;
    cumulative_income: number;
    avg_annualized_yield: number;
    current_option_buyback: number;
    net_option_income: number;
  };
  harvest_tax_benefits: {
    net_realized_tlh_losses: number;
    losses_used: number;
    losses_remaining: number;
    shares_sold: number;
    sellable_today: number;
  };
}

/** Strategy report themed sections; mirrors keys returned by the new-report API for report_type strategy. */
export interface NewReportStrategyThemedCards {
  keep: {
    shares_start: number;
    shares_end: number;
    value_start: number;
    value_end: number;
    dividends: number;
    upside_participation: number;
    basis: number;
    tax_savings: number;
  };
  protect: {
    max_stock_drop: number;
    oea_drop: number;
    collar_pl: number;
    available_margin_balance: number;
  };
  diversify: {
    overall_diversified_performance: number;
    tax_loss_harvest_sleeve: number;
    beginning_concentration: number;
    current_concentration: number;
    holdings: number;
  };
  harvest: {
    realized_losses: number;
    losses_used: number;
    pool_remaining: number;
    unrealized_gains: number;
    shares_sold: number;
    sellable_today: number;
  };
}

export interface NewReportResponse {
  status: "success";
  data: {
    single_info_cards: NewReportSingleInfoCards;
    graph_or_table_data: ReportChartDataPoint[];
    themed_cards?: NewReportThemedCards | NewReportStrategyThemedCards;
    excel_url?: string;
  };
}

/** PDF report request: matches backend PDFReportSerializer */
export type ReportType = "stock" | "strategy";

export interface PDFReportSettingsInfo {
  client_name: string;
  client_id?: number | null;
  account_id?: number | null;
  account: string;
  period: [string, string];
  global_cumulative_period?: [string, string];
  symbol: string;
  strategy_id?: number | null;
  orion_account_id?: number | null;
}

export interface PDFReportTableColumnRow {
  id: number;
  Period: string;
  "Stock Price": number;
  "Option Income": number;
  "Dividend Income": number;
  "Total Income": number;
  Comment: string;
}

export interface ReportChartDataPoint {
  date: string;
  option_total: number;
  stock_total: number;
}

export enum PDFReportValueType {
  NUMBER = "number",
  CURRENCY = "currency",
  PERCENTAGE = "percentage",
  SHARES = "shares",
}

export enum PDFReportChartType {
  BAR = "bar",
  DONUT = "donut",
}

export interface PDFReportClientInfoItem {
  label: string;
  value: string | number;
  type: PDFReportValueType;
  description: string;
}

export interface PDFReportValueItem {
  label: string;
  value: number;
  type: PDFReportValueType;
}

export interface PDFReportAddictionalComponent {
  type: PDFReportChartType;
  title: string;
  limit: number;
  negativeLimit?: number;
  data: PDFReportValueItem[];
}

export interface PDFReportThemeInfoItem {
  label: string;
  description: string;
  values: PDFReportValueItem[];
  addictional_component?: PDFReportAddictionalComponent | null;
}

export interface PDFReportRequest {
  type: ReportType;
  settings_info?: PDFReportSettingsInfo;
  table_columns?: PDFReportTableColumnRow[];
  chart_data?: ReportChartDataPoint[];
  client_info: PDFReportClientInfoItem[];
  theme_info?: PDFReportThemeInfoItem[];
}

// ALERT DOMAIN TYPES AND HELPERS
export type AlertLeg = {
  action?: string;
  expiration_date?: string;
  strike_price?: number | string | null;
};

export type AlertPayload = {
  contract?: {
    legs?: AlertLeg[];
  };
  [key: string]: unknown;
};

export type AlertType = {
  id: number;
  type?: string | null;
  status?: string | null;
  seen?: boolean;
  time_sent?: string | null;
  order_id?: string | null;
  symbol?: string | null;
  contract_type?: "C" | "P" | string | null;
  net_avg_price?: number | string | null;
  auto_extension_percentage?: number | null;
  auto_partial_close_percentage?: number | null;
  legs?: AlertLeg[];
  payload?: AlertPayload | string | null;
};

export const asText = (value: unknown) =>
  value === null || value === undefined ? "—" : String(value);

export const formatMoney = (value: unknown, decimals = 4) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(decimals)}`;
};

export const contractLabel = (contractType?: string | null) => {
  if (contractType === "C") return "Call";
  if (contractType === "P") return "Put";
  return asText(contractType ?? "");
};

export enum PerformanceReportTypeEnum {
  STOCK = "stock",
  STRATEGY = "strategy",
}

export enum AdjustmentTypeEnum {
  INCEPTION = "INCEPTION",
  BUY = "BUY",
  SELL = "SELL",
  REMOVED = "REMOVED",
  MERGE_IN = "MERGE_IN",
  JOURNAL_IN = "JOURNAL_IN",
  JOURNAL_OUT = "JOURNAL_OUT",
  MAX_TRADE_AMOUNT = "MAX_TRADE_AMOUNT",
}

export enum BalanceTypeEnum {
  TRADE_AMOUNT = "TRADE_AMOUNT",
  PORTFOLIO = "PORTFOLIO",
}

export interface AdjustmentRow {
  id: number;
  adjustment_date: string;
  adjustment_type: AdjustmentTypeEnum;
  adjustment_amount: number;
  unit_balance: number;
  balance_type: BalanceTypeEnum;
  ignore: boolean;
}

export interface AccountHistoryRecord {
  id: number;
  account: number;
  strategy: number;
  adjustment_date: string;
  adjustment_type: string;
  adjustment_amount: number;
  unit_balance_after_adjustment: number;
  balance_type: string;
  ignore: boolean;
  created_on?: string;
  created_by?: number | null;
  updated_on?: string;
  updated_by?: number | null;
}

export interface AccountHistoryQueryParams {
  account_id: number;
  strategy_id: number;
  ordering?: string;
  balance_type?: string;
  adjustment_type?: string;
}

export interface CreateAccountHistoryPayload {
  account: number;
  strategy: number;
  adjustment_date: string;
  adjustment_type: string;
  adjustment_amount: number;
  balance_type?: string;
  ignore?: boolean;
}

export interface SingleValueCardProps {
  label: string;
  value: number;
  description: string;
  type: SingleValueCardType;
  color: SingleValueCardColor;
  onValueChange: (value: number) => void;
}
export interface MultiValuesCardProps {
  title: string;
  values: ReportValueCardProps[];
  description: string;
  additionalComponents: React.ReactNode | null;
}

export interface ReportValueCardProps {
  label: string;
  value: number | string;
  type: SingleValueCardType;
  onValueChange: (value: number | string) => void;
  fullWidthRow?: boolean;
  color?: SingleValueCardColor;
}

export interface ReportThemedCardConfig {
  label: string;
  description: string;
  values: ReportValueCardProps[];
  addictional_component: PDFReportAddictionalComponent | null;
}

export interface ReportPdfResponse {
  download_url?: string;
  [key: string]: string[] | unknown;
}

export interface OrionTransactionParams {
  start_date: string;
  end_date: string;
  strategy_id: number;
  orion_account_id: number;
  report_type: ReportType;
  transaction_type_ids?: number[];
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
}

export interface OrionTransaction {
  id: number;
  status: string;
  transaction_type_id: number;
  transaction_date: string;
  settlement_date: string;
  transaction_amount: number;
  units_amount: number;
  nav_price: number;
  ticker: string;
  transaction_type: string;
  unit_balance: number;
}

export enum OrionTransactionTypeIds {
  BUY_EXCHANGE = 10,
  SELL_EXCHANGE = 53,
}
